import { useState, useEffect } from 'react'
import CmdbHandlers from './CmdbHandlers'
import CmdbMappingProfiles from './CmdbMappingProfiles'
import CmdbOutboundProfiles from './CmdbOutboundProfiles'
import CmdbImportExport from './CmdbImportExport'

type Tab = 'handlers' | 'profiles' | 'importexport'
type ProfileDir = 'inbound' | 'outbound'

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'handlers', label: 'Handlers', icon: 'hub' },
  { key: 'profiles', label: 'Profiles', icon: 'account_tree' },
  { key: 'importexport', label: 'Import / Export', icon: 'import_export' },
]

export default function CmdbPage() {
  const [tab, setTab] = useState<Tab>('handlers')
  const [profileDir, setProfileDir] = useState<ProfileDir>('inbound')
  const [showCreate, setShowCreate] = useState(false)

  useEffect(() => { setShowCreate(false) }, [tab])

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text-light dark:text-text-dark">CMDB</h1>
          <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
            Bidirectional sync between Quantizant inventories and your CMDB.
          </p>
        </div>
        {tab === 'handlers' && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
          >
            <span className="material-icons text-base">add</span>
            New Handler
          </button>
        )}
        {tab === 'profiles' && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
          >
            <span className="material-icons text-base">add</span>
            New Profile
          </button>
        )}
      </div>

      <div className="border-b border-border-light dark:border-border-dark">
        <nav className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
              }`}
            >
              <span className="material-icons text-sm">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'handlers' && (
        <CmdbHandlers asTab showCreate={showCreate} onShowCreateChange={setShowCreate} />
      )}

      {tab === 'profiles' && (
        <div className="space-y-5">
          <div className="flex rounded-xl overflow-hidden w-fit">
            <button
              onClick={() => setProfileDir('inbound')}
              className={`px-5 py-2 text-sm font-medium transition-colors ${
                profileDir === 'inbound'
                  ? 'bg-primary text-white'
                  : 'text-subtle-light dark:text-subtle-dark hover:bg-primary/5'
              }`}
            >
              Inbound
            </button>
            <button
              onClick={() => setProfileDir('outbound')}
              className={`px-5 py-2 text-sm font-medium transition-colors ${
                profileDir === 'outbound'
                  ? 'bg-primary text-white'
                  : 'text-subtle-light dark:text-subtle-dark hover:bg-primary/5'
              }`}
            >
              Outbound
            </button>
          </div>
          {profileDir === 'inbound' ? (
            <CmdbMappingProfiles asTab showCreate={showCreate} onShowCreateChange={setShowCreate} />
          ) : (
            <CmdbOutboundProfiles asTab showCreate={showCreate} onShowCreateChange={setShowCreate} />
          )}
        </div>
      )}

      {tab === 'importexport' && <CmdbImportExport asTab />}
    </div>
  )
}
